import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { InfoCard } from '@backstage/core-components';
import {
  Box,
  Chip,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@material-ui/core';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import BlockIcon from '@material-ui/icons/Block';

const useStyles = makeStyles(theme => ({
  label: {
    fontWeight: 600,
    whiteSpace: 'nowrap',
    width: '45%',
    color: theme.palette.text.secondary,
    fontSize: '0.8rem',
  },
  value: {
    fontSize: '0.85rem',
  },
  approvalChip: {
    backgroundColor: '#e53935',
    color: '#fff',
    height: 20,
    fontSize: '0.7rem',
  },
  noApprovalChip: {
    backgroundColor: '#1db954',
    color: '#fff',
    height: 20,
    fontSize: '0.7rem',
  },
  levelBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 700,
    fontSize: '0.8rem',
  },
}));

const CATEGORY_LABELS: Record<string, string> = {
  development: 'Development',
  testing: 'Testing',
  staging: 'Staging',
  sandbox: 'Sandbox',
  production: 'Production',
};

const CLASSIFICATION_LABELS: Record<string, string> = {
  private: 'Private',
  internal: 'Internal',
  public: 'Public',
};

const ENV_TYPE_LABELS: Record<string, string> = {
  databricks: 'Azure Databricks',
  aks: 'Azure Kubernetes Service',
  'container-apps': 'Azure Container Apps',
  functions: 'Azure Functions',
  'app-service': 'Azure App Service',
};

interface InfoRowProps {
  label: string;
  children: React.ReactNode;
}

function InfoRow({ label, children }: InfoRowProps) {
  const classes = useStyles();
  return (
    <TableRow>
      <TableCell className={classes.label}>{label}</TableCell>
      <TableCell className={classes.value}>{children}</TableCell>
    </TableRow>
  );
}

export function AzureEnvironmentInfoCard() {
  const classes = useStyles();
  const { entity } = useEntity();
  const m = entity.metadata as Record<string, any>;

  const requiresApproval =
    m.deploymentRequiresApproval === true ||
    m.deploymentRequiresApproval === 'true';

  const level = Number(m.level);

  return (
    <InfoCard title="Environment Info">
      <Table size="small">
        <TableBody>
          <InfoRow label="Short Name">
            {m.shortName ?? m.name}
          </InfoRow>
          <InfoRow label="Environment Type">
            {ENV_TYPE_LABELS[m.environmentType] ?? m.environmentType ?? '—'}
          </InfoRow>
          <InfoRow label="Category">
            {CATEGORY_LABELS[m.category] ?? m.category ?? '—'}
          </InfoRow>
          <InfoRow label="Classification">
            {CLASSIFICATION_LABELS[m.classification] ?? m.classification ?? '—'}
          </InfoRow>
          <InfoRow label="Hierarchy Level">
            {!isNaN(level) && level > 0 ? (
              <span className={classes.levelBadge}>{level}</span>
            ) : (
              '—'
            )}
          </InfoRow>
          <InfoRow label="Deployment Approval">
            {requiresApproval ? (
              <Chip
                icon={<BlockIcon style={{ fontSize: 13 }} />}
                label="Approval required"
                size="small"
                className={classes.approvalChip}
              />
            ) : (
              <Chip
                icon={<CheckCircleOutlineIcon style={{ fontSize: 13 }} />}
                label="No approval needed"
                size="small"
                className={classes.noApprovalChip}
              />
            )}
          </InfoRow>
        </TableBody>
      </Table>
      <Box pb={1} />
    </InfoCard>
  );
}
